/**
 * useAttentionCounts
 *
 * Computes live "needs attention" counts for the item-management sidebar badges:
 *   - triageCount       — same visibility as Triage Queue: intake + active (not deferred/archived),
 *                        excluding items with a finalized triage assessment (except needs_clarification)
 *   - needsShapingCount — items missing category OR altitude level (the "Needs Definition" page)
 *
 * triageCount must match `/prioritization/intake/triage` (useRealQueueItems + assessment filter).
 */

import { useMemo } from 'react';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useTriageStore } from '../../stores/prioritization/triageStore';
import {
  itemIsAwaitingTriage,
  itemNeedsDefinition,
  triageFinalizedOutOfQueueIds,
} from '../../lib/intakeQueueVisibility';

export interface AttentionCounts {
  triageCount: number;
  needsShapingCount: number;
}

export function useAttentionCounts(): AttentionCounts {
  const items = useItemsStore((s) => s.items);
  const assessments = useTriageStore((s) => s.assessments);

  return useMemo<AttentionCounts>(() => {
    const triagedOut = triageFinalizedOutOfQueueIds(assessments);

    let triageCount = 0;
    let needsShapingCount = 0;

    for (const item of items) {
      if (itemIsAwaitingTriage(item, triagedOut)) triageCount += 1;
      // Same predicate as the Needs Definition page and the Pipeline Health funnel.
      if (itemNeedsDefinition(item)) needsShapingCount += 1;
    }

    return { triageCount, needsShapingCount };
  }, [items, assessments]);
}
